// RITA-1599 — Hinweise zu typischen Fehlern in der Vorlage (Wortlaut mit Julia am 03.11.2026 abgestimmt).
//
// Der gelbe Kasten steht direkt unter der Zuordnung und über dem Arbeitsbereich; er klebt nicht und erscheint schon
// beim Schreiben. „Stelle zeigen“ springt der Reihe nach zu den Fundstellen eines Hinweises (bei mehreren Stellen
// beim nächsten Klick zur nächsten) und markiert sie. Beim Speichern fragt `HinweiseSpeichernDialog` nach, solange
// Hinweise da sind: „Zurück zur Vorlage“ oder „Trotzdem speichern“ (ausgefüllt). Gesperrt wird nichts.
import React, { RefObject, useRef } from 'react';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Typography,
} from '@mui/material';
import {
  HINWEISE_ANZEIGEN,
  HINWEISE_TITEL,
  hinweisleisteText,
  SPEICHERN_FRAGE_HINWEIS,
  SPEICHERN_FRAGE_TITEL,
  speichernFrageText,
  STELLE_ZEIGEN,
  TROTZDEM_SPEICHERN,
  ZURUECK_ZUR_VORLAGE,
} from '../../services/vorlagenhinweiseTexte';
import { Auswahl } from '../model/positionen';
import { Vorlagenhinweis } from '../model/vorlagenpruefung';

/** Knopftexte wie abgestimmt schreiben; das App-Design setzt sonst jedes Wort groß. */
const KNOEPFE = { '& .MuiButton-root': { textTransform: 'none' } } as const;

interface KastenProps {
  hinweise: Vorlagenhinweis[];
  gesperrt: boolean;
  /** Der Kasten selbst – „Anzeigen“ in der festen Leiste holt ihn zurück ins Bild. */
  kastenRef?: RefObject<HTMLDivElement>;
  /** Markiert die Stelle im Editor und holt sie ins Bild. */
  onStelleZeigen: (stelle: Auswahl) => void;
}

export function Vorlagenhinweise({ hinweise, gesperrt, kastenRef, onStelleZeigen }: KastenProps) {
  // Je Hinweis die zuletzt gezeigte Stelle; der nächste Klick nimmt die folgende (nach der letzten wieder die erste).
  const gezeigt = useRef(new Map<string, number>());
  if (hinweise.length === 0) return null;
  const zeigen = (hinweis: Vorlagenhinweis) => {
    const naechste = ((gezeigt.current.get(hinweis.id) ?? -1) + 1) % hinweis.stellen.length;
    gezeigt.current.set(hinweis.id, naechste);
    onStelleZeigen(hinweis.stellen[naechste]);
  };
  return (
    <Alert
      severity="warning"
      role="region"
      aria-labelledby="vorlagenhinweise-titel"
      data-testid="vorlagenhinweise"
      ref={kastenRef}
      tabIndex={-1}
      sx={{ mb: 2, '& .MuiAlert-message': { flex: 1, minWidth: 0 } }}
    >
      <AlertTitle id="vorlagenhinweise-titel">{HINWEISE_TITEL}</AlertTitle>
      <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
        {hinweise.map((hinweis) => (
          <Box component="li" key={hinweis.id} data-hinweis={hinweis.art} sx={{ mb: 0.5, '&:last-child': { mb: 0 } }}>
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 2, flexWrap: 'wrap' }}>
              <Typography variant="body2" sx={{ flex: '1 1 320px', minWidth: 0, overflowWrap: 'anywhere' }}>
                {hinweis.text}
              </Typography>
              {hinweis.stellen.length > 0 && (
                <Button
                  size="small"
                  color="inherit"
                  disabled={gesperrt}
                  onClick={() => zeigen(hinweis)}
                  sx={{ textTransform: 'none', textDecoration: 'underline', flexShrink: 0, py: 0 }}
                >
                  {STELLE_ZEIGEN}
                </Button>
              )}
            </Box>
          </Box>
        ))}
      </Box>
    </Alert>
  );
}

interface DialogProps {
  /** Die offenen Hinweise; `null` = Rückfrage geschlossen. */
  hinweise: Vorlagenhinweis[] | null;
  onZurueck: () => void;
  onTrotzdemSpeichern: () => void;
}

export function HinweiseSpeichernDialog({ hinweise, onZurueck, onTrotzdemSpeichern }: DialogProps) {
  // Beim Schließen blendet MUI das Fenster noch aus: bis dahin die letzte Liste zeigen, nicht „0 Stellen“.
  const letzte = useRef<Vorlagenhinweis[]>([]);
  if (hinweise) letzte.current = hinweise;
  const liste = hinweise ?? letzte.current;
  return (
    <Dialog
      open={hinweise !== null}
      onClose={onZurueck}
      aria-labelledby="vorlagenhinweise-speichern-titel"
      data-testid="vorlagenhinweise-speichern-dialog"
    >
      <DialogTitle id="vorlagenhinweise-speichern-titel">{SPEICHERN_FRAGE_TITEL}</DialogTitle>
      <DialogContent>
        <DialogContentText data-testid="vorlagenhinweise-speichern-text">
          {speichernFrageText(liste.length)}
        </DialogContentText>
        {/* Die Liste steht neben dem Text, nicht darin: DialogContentText ist ein Absatz. */}
        <Box
          component="ul"
          data-testid="vorlagenhinweise-speichern-liste"
          sx={{ my: 0.5, pl: 2.5, color: 'text.secondary' }}
        >
          {liste.map((hinweis) => (
            <li key={hinweis.id}>{hinweis.text}</li>
          ))}
        </Box>
        <Typography sx={{ mt: 1.5, fontSize: 13, color: 'text.secondary' }}>{SPEICHERN_FRAGE_HINWEIS}</Typography>
      </DialogContent>
      <DialogActions sx={KNOEPFE}>
        <Button onClick={onZurueck}>{ZURUECK_ZUR_VORLAGE}</Button>
        <Button variant="contained" onClick={onTrotzdemSpeichern} data-testid="vorlagenhinweise-trotzdem-speichern">
          {TROTZDEM_SPEICHERN}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

interface LeisteProps {
  anzahl: number;
  /** Holt den gelben Kasten zurück ins Bild (unter die feste Titelzeile). */
  onAnzeigen: () => void;
}

/** Orange Leiste im festen Titelbereich: Anzahl der Hinweise und „Anzeigen“ (Skizze A, Julia 03.11.2026). */
export function VorlagenhinweiseLeiste({ anzahl, onAnzeigen }: LeisteProps) {
  return (
    <Alert
      severity="warning"
      variant="filled"
      role="status"
      data-testid="vorlagenhinweise-leiste"
      action={
        <Button color="inherit" size="small" onClick={onAnzeigen} sx={{ textTransform: 'none', fontWeight: 600 }}>
          {HINWEISE_ANZEIGEN}
        </Button>
      }
      sx={{
        mt: 0.5,
        py: 0,
        alignItems: 'center',
        '& .MuiAlert-icon': { py: 0.5 },
        '& .MuiAlert-message': { py: 0.5 },
        '& .MuiAlert-action': { pt: 0, mr: 0 },
      }}
    >
      {hinweisleisteText(anzahl)}
    </Alert>
  );
}
